
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Flag, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthModalLoginForm } from "@/components/auth/AuthModalLoginForm";
import { AuthModalGoogleButton } from "@/components/auth/AuthModalGoogleButton";
import { AuthModalDivider } from "@/components/auth/AuthModalDivider";
import { useToast } from "@/hooks/use-toast";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Get the redirect path properly from location state
  const from = location.state?.from || "/";
  console.log("Login page - Redirect path:", from);
  
  // Only redirect if user is already logged in AND we've explicitly set shouldRedirect
  useEffect(() => {
    if (user && !authLoading && !shouldRedirect) {
      console.log("Login page - User already logged in, but will stay on login page until action is taken");
    }
    
    if (user && !authLoading && shouldRedirect) {
      console.log("Login page - User logged in and redirect triggered, redirecting to:", from);
      // Add small delay to ensure auth state is fully updated
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 200);
    }
  }, [user, authLoading, from, navigate, shouldRedirect]);

  const handleLoginSuccess = () => {
    console.log("Login page - Login success handler called, setting shouldRedirect flag");
    setShouldRedirect(true);
    // The redirect will be handled by the useEffect above
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  const handleToggleSignup = () => {
    // Preserve the redirect target when switching to signup
    navigate('/signup', { state: { from } });
  };

  // If auth is loading, show minimal UI
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading authentication...</p>
      </div>
    );
  }

  // If user is already logged in AND we've triggered a redirect, show minimal UI
  if (user && shouldRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Redirecting...</p>
      </div>
    );
  }

  // Otherwise show the login form, even if user is logged in
  // This allows users to log in with a different account if needed
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <div className="p-2 bg-redflags-red/10 rounded-full">
                <Flag className="w-8 h-8 text-redflags-red" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-redflags-dark">
              Welcome back
            </h1>
            <p className="text-redflags-gray mt-2">
              Log in to access RedFlags
            </p>
            {from !== "/" && (
              <p className="text-sm text-redflags-red mt-2">
                Sign in to continue to your previous action
              </p>
            )}
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <AuthModalLoginForm onSuccess={handleLoginSuccess} onClose={() => {}} />

            <AuthModalDivider />

            <AuthModalGoogleButton className="w-full mt-4" />
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              <span>Don't have an account?</span>{' '}
              <Button variant="link" className="p-0 h-auto" onClick={handleToggleSignup}>
                Create one now
              </Button>
            </p>
          </div>
          
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={handleReturnHome}
            >
              <Home className="w-4 h-4" />
              Return to Home
            </Button>
          </div>
          
          <footer className="mt-10 pt-6 text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} RedFlags. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Login;
